import { useState } from "react";

import DeleteIcon from "@mui/icons-material/Delete";
import { FormControlLabel, Switch, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import { useTheme } from "@mui/material/styles";
import { v4 as uuid } from "uuid";

import Dialog from "../ui/omniDialog";
import DialogActions from "../ui/omniDialogActions";
import DialogContent from "../ui/omniDialogContent";
import DialogTitle from "../ui/omniDialogTitle";
import OmniResponsiveButton from "../ui/omniResponsiveButton";
import OmniTextField from "../ui/omniTextField";
import AlertDialog from "./alertDialog";

const isEmptyFieldValue = value => {
  if (value == null || value === "") {
    return true;
  }

  if (Array.isArray(value)) {
    return value.length === 0;
  }

  if (typeof value === "object") {
    return Object.values(value).every(item => isEmptyFieldValue(item));
  }

  return false;
};

const formatObjectField = value => {
  if (isEmptyFieldValue(value)) {
    return "";
  }

  return JSON.stringify(value, null, 2);
};

const parseStringField = value => {
  if (value == null || value.trim() === "") {
    return null;
  }

  return value.trim();
};

const parseRequiredStringField = value => {
  return parseStringField(value) || "";
};

const parseNumberField = value => {
  if (value == null || value.toString().trim() === "") {
    return null;
  }

  const parsedValue = Number(value);

  if (Number.isNaN(parsedValue)) {
    return null;
  }

  return parsedValue;
};

const isValidNumberField = value => {
  if (value == null || value.toString().trim() === "") {
    return true;
  }

  return !Number.isNaN(Number(value));
};

const isValidJsonField = value => {
  if (value == null || value.trim() === "") {
    return true;
  }

  try {
    JSON.parse(value);
    return true;
  } catch {
    return false;
  }
};

const isValidDateField = value => {
  if (value == null || value.trim() === "") {
    return true;
  }

  return /^\d{4}-\d{2}-\d{2}$/.test(value.trim());
};

const parseObjectField = (value, fallbackValue = null) => {
  if (value == null || value.trim() === "") {
    return fallbackValue;
  }

  try {
    return JSON.parse(value);
  } catch {
    return fallbackValue;
  }
};

const parseMetadataField = value => {
  const parsedMetadata = parseObjectField(value, null);

  if (parsedMetadata == null) {
    return null;
  }

  const cleanedMetadata = {
    campaignPriority: parseNumberField(parsedMetadata.campaignPriority),
    vertical: parseStringField(parsedMetadata.vertical),
  };

  if (isEmptyFieldValue(cleanedMetadata)) {
    return null;
  }

  return cleanedMetadata;
};

const parseMarkersField = value => {
  if (value == null || value.trim() === "") {
    return [];
  }

  try {
    const parsedValue = JSON.parse(value);

    if (Array.isArray(parsedValue)) {
      return parsedValue;
    }

    return [parsedValue];
  } catch {
    return [];
  }
};

const cleanProductRecord = productRecord => {
  const {
    computed,
    stale,
    description,
    price,
    state,
    matchSignals,
    softRequirements,
    ...cleanProductRecord
  } = productRecord || {};

  return cleanProductRecord;
};

const BotpressProductsDialog = props => {
  const {
    open,
    productRecord,
    onClose,
    onAccept,
    onDelete,
    dialogTitle = "Edit Product",
    okMessage = "Ok",
    cancelMessage = "Cancel",
    maxWidth = "md",
    fullWidth = true,
    deleteMessage = "Delete",
  } = props;

  const theme = useTheme();

  const [type, setType] = useState(productRecord.type || "");
  const [archived, setArchived] = useState(productRecord.archived || false);
  const [name, setName] = useState(productRecord.name || "");
  const [title, setTitle] = useState(productRecord.title || "");
  const [summary, setSummary] = useState(productRecord.summary || "");
  const [startDate, setStartDate] = useState(productRecord.startDate || "");
  const [endDate, setEndDate] = useState(productRecord.endDate || "");
  const [cta, setCta] = useState(formatObjectField(productRecord.cta));
  const [assets, setAssets] = useState(formatObjectField(productRecord.assets));
  const [metadata, setMetadata] = useState(formatObjectField(productRecord.metadata));
  const [priority, setPriority] = useState(
    productRecord.priority == null ? "" : productRecord.priority,
  );
  const [markers, setMarkers] = useState(formatObjectField(productRecord.markers));
  const [showConfirm, setShowConfirm] = useState(false);

  const priorityHasError = !isValidNumberField(priority);
  const startDateHasError = !isValidDateField(startDate);
  const endDateHasError = !isValidDateField(endDate);
  const ctaHasError = !isValidJsonField(cta);
  const assetsHasError = !isValidJsonField(assets);
  const metadataHasError = !isValidJsonField(metadata);
  const markersHasError = !isValidJsonField(markers);

  const formHasError =
    priorityHasError ||
    startDateHasError ||
    endDateHasError ||
    ctaHasError ||
    assetsHasError ||
    metadataHasError ||
    markersHasError;

  const onDeleteHandler = () => {
    onDelete(productRecord);
  };

  const onAcceptHandler = () => {
    if (formHasError) {
      return;
    }

    try {
      const editedProductRecord = cleanProductRecord({
        ...productRecord,

        type: parseRequiredStringField(type),
        archived: archived,
        name: parseRequiredStringField(name),
        title: parseRequiredStringField(title),

        summary: parseStringField(summary),
        startDate: parseStringField(startDate),
        endDate: parseStringField(endDate),
        cta: parseObjectField(cta, null),
        assets: parseObjectField(assets, null),
        metadata: parseMetadataField(metadata),
        markers: parseMarkersField(markers),
      });

      const parsedPriority = parseNumberField(priority);

      if (parsedPriority != null) {
        editedProductRecord.priority = parsedPriority;
      } else {
        delete editedProductRecord.priority;
      }

      onAccept(editedProductRecord);
    } catch (error) {
      console.log(error);
    }
  };

  const onCloseHandler = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          onCloseHandler();
        }
      }}
      disableEscapeKeyDown
      maxWidth={maxWidth}
      fullWidth={fullWidth}
    >
      <DialogTitle onClose={onCloseHandler}>{dialogTitle}</DialogTitle>

      <DialogContent>
        <Grid spacing={1} container size={{ xs: 12 }}>
          {productRecord.id != null && (
            <Grid item size={{ xs: 12 }}>
              <Typography variant="body2" color="textSecondary">
                Product ID: {productRecord.id}
              </Typography>
            </Grid>
          )}

          <Grid item container size={{ xs: 6 }}>
            <OmniTextField
              sx={{ width: "100%" }}
              label={"Name"}
              type="textInput"
              variant="outlined"
              name={uuid()}
              id={"productName"}
              value={name}
              onChange={event => setName(event.target.value)}
            />
          </Grid>

          <Grid item container size={{ xs: 6 }}>
            <OmniTextField
              sx={{ width: "100%" }}
              label={"Type"}
              type="textInput"
              variant="outlined"
              name={uuid()}
              id={"productType"}
              value={type}
              onChange={event => setType(event.target.value)}
            />
          </Grid>

          <Grid item container size={{ xs: 12 }}>
            <OmniTextField
              sx={{ width: "100%" }}
              label={"Title"}
              type="textInput"
              variant="outlined"
              name={uuid()}
              id={"productTitle"}
              value={title}
              onChange={event => setTitle(event.target.value)}
            />
          </Grid>

          <Grid item container size={{ xs: 12 }}>
            <OmniTextField
              sx={{ width: "100%" }}
              label={"Summary"}
              type="textInput"
              variant="outlined"
              name={uuid()}
              id={"productSummary"}
              rowCount={4}
              multiline={true}
              value={summary}
              onChange={event => setSummary(event.target.value)}
            />
          </Grid>

          <Grid item container size={{ xs: 6 }}>
            <OmniTextField
              sx={{ width: "100%" }}
              label={"Start Date"}
              type="textInput"
              variant="outlined"
              name={uuid()}
              id={"productStartDate"}
              value={startDate}
              error={startDateHasError}
              helperText={
                startDateHasError ? "Start Date must use YYYY-MM-DD, like 2026-01-01." : ""
              }
              onChange={event => setStartDate(event.target.value)}
            />
          </Grid>

          <Grid item container size={{ xs: 6 }}>
            <OmniTextField
              sx={{ width: "100%" }}
              label={"End Date"}
              type="textInput"
              variant="outlined"
              name={uuid()}
              id={"productEndDate"}
              value={endDate}
              error={endDateHasError}
              helperText={endDateHasError ? "End Date must use YYYY-MM-DD, like 2026-01-01." : ""}
              onChange={event => setEndDate(event.target.value)}
            />
          </Grid>

          <Grid item container size={{ xs: 6 }}>
            <OmniTextField
              sx={{ width: "100%" }}
              label={"Priority"}
              type="textInput"
              variant="outlined"
              name={uuid()}
              id={"productPriority"}
              value={priority}
              error={priorityHasError}
              helperText={priorityHasError ? "Priority must be a number." : ""}
              onChange={event => setPriority(event.target.value)}
            />
          </Grid>

          <Grid item container size={{ xs: 6 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={archived}
                  onChange={event => setArchived(event.target.checked)}
                  color="secondary"
                />
              }
              label={"Archived"}
            />
          </Grid>

          <Grid item container size={{ xs: 12 }}>
            <OmniTextField
              sx={{ width: "100%" }}
              label={"CTA"}
              type="textInput"
              variant="outlined"
              name={uuid()}
              id={"productCta"}
              rowCount={4}
              multiline={true}
              value={cta}
              error={ctaHasError}
              helperText={ctaHasError ? "CTA must be valid JSON." : ""}
              onChange={event => setCta(event.target.value)}
            />
          </Grid>

          <Grid item container size={{ xs: 12 }}>
            <OmniTextField
              sx={{ width: "100%" }}
              label={"Assets"}
              type="textInput"
              variant="outlined"
              name={uuid()}
              id={"productAssets"}
              rowCount={4}
              multiline={true}
              value={assets}
              error={assetsHasError}
              helperText={assetsHasError ? "Assets must be valid JSON." : ""}
              onChange={event => setAssets(event.target.value)}
            />
          </Grid>

          <Grid item container size={{ xs: 12 }}>
            <OmniTextField
              sx={{ width: "100%" }}
              label={"Metadata"}
              type="textInput"
              variant="outlined"
              name={uuid()}
              id={"productMetadata"}
              rowCount={4}
              multiline={true}
              value={metadata}
              error={metadataHasError}
              helperText={metadataHasError ? "Metadata must be valid JSON." : ""}
              onChange={event => setMetadata(event.target.value)}
            />
          </Grid>

          <Grid item container size={{ xs: 12 }}>
            <OmniTextField
              sx={{ width: "100%" }}
              label={"Markers"}
              type="textInput"
              variant="outlined"
              name={uuid()}
              id={"productMarkers"}
              rowCount={5}
              multiline={true}
              value={markers}
              error={markersHasError}
              helperText={markersHasError ? "Markers must be valid JSON." : ""}
              onChange={event => setMarkers(event.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Box
          sx={{
            paddingRight: theme.spacing(2),
            paddingBottom: 0,
            display: "flex",
            flexDirection: "row",
            userSelect: "none",
            webkitUserSelect: "none",
            position: "relative",
            flexGrow: 1,
          }}
        >
          {productRecord.id != null && props.onDelete != null && (
            <OmniResponsiveButton
              icon={<DeleteIcon />}
              onClick={() => setShowConfirm(true)}
              buttonText={deleteMessage}
              tooltipTitle=""
            />
          )}
        </Box>

        <Box
          sx={{
            flexGrow: 0,
            flexShrink: 1,
          }}
        >
          {cancelMessage && (
            <Button
              onClick={onCloseHandler}
              color="secondary"
              variant="contained"
              sx={{ marginRight: theme.spacing(2) }}
            >
              {cancelMessage}
            </Button>
          )}

          <Button
            onClick={onAcceptHandler}
            color="secondary"
            variant="contained"
            disabled={formHasError}
          >
            {okMessage}
          </Button>
        </Box>
      </DialogActions>

      {showConfirm && (
        <AlertDialog
          open={showConfirm}
          dialogTitle={`Confirm`}
          dialogMessage={"Are you sure you want to delete this Product?"}
          onAccept={() => {
            setShowConfirm(false);
            onDeleteHandler();
          }}
          onClose={() => {
            setShowConfirm(false);
          }}
        />
      )}
    </Dialog>
  );
};

export default BotpressProductsDialog;
